import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/dashboard/PageHeader';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Images,
  Play,
  RefreshCw,
} from 'lucide-react';
import {
  ASSET_KINDS,
  fetchBrandAssets,
  fetchDesignRequests,
  signAssetPaths,
  displayUrl,
  mediaUrl,
  assetPaths,
  parseNotes,
  isLightAsset,
  distinctBusinesses,
  filterAssets,
  formatBytes,
} from '@/lib/brandData';

/* K.O. Command brand library (069). The registered, approved brand files
 * Milli's design department composes with, plus the department's recent
 * queue. Read-only by design: assets enter the library through the approval
 * trigger or by hand, never from a button here. */

const REQUEST_TONE = {
  pending: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  generating: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  ready: 'bg-sky-500/15 text-sky-600 border-sky-500/30',
  approved: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  rejected: 'bg-muted text-muted-foreground border-border',
  failed: 'bg-red-500/15 text-red-600 border-red-500/30',
};

const formatWhen = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-3 py-1 text-xs uppercase tracking-[0.12em] transition-colors ${
        active
          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
          : 'border-border text-muted-foreground hover:bg-muted/60'
      }`}
    >
      {children}
    </button>
  );
}

function AssetCard({ asset, url, fileUrl, t }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rows = useMemo(() => parseNotes(asset.notes), [asset.notes]);
  const light = isLightAsset(asset);
  const isVideo = asset.kind === 'video';
  // The link and the copied address are the file itself (the MP4 for a
  // video); the thumbnail is the poster frame.
  const link = fileUrl || url;

  const copyUrl = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be refused in some contexts; the Open link still works.
    }
  };

  return (
    <Card data-testid={`brand-asset-${asset.name}`} className="flex flex-col">
      <div
        className={`relative flex aspect-square items-center justify-center border-b border-border p-3 ${
          light ? 'bg-[#EDEBE6]' : 'bg-[#1e2023]'
        }`}
      >
        {url ? (
          <img
            src={url}
            alt={asset.name}
            loading="lazy"
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <span className="text-xs text-muted-foreground">{t('brand.noPreview')}</span>
        )}
        {isVideo ? (
          <span
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
            data-testid={`brand-asset-play-${asset.name}`}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/55 text-white shadow-lg">
              <Play size={20} className="ml-0.5" fill="currentColor" />
            </span>
          </span>
        ) : null}
        {isVideo && asset.duration_seconds ? (
          <span className="absolute bottom-2 right-2 rounded bg-black/60 px-1.5 py-0.5 font-mono text-[11px] text-white">
            {Math.round(Number(asset.duration_seconds))} s
          </span>
        ) : null}
      </div>
      <CardContent className="flex flex-1 flex-col gap-2.5 pt-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline">{asset.business}</Badge>
          <Badge variant="outline" className="text-muted-foreground">
            {t(`brand.kind.${asset.kind}`, asset.kind)}
          </Badge>
        </div>
        <code className="break-all text-[13px] text-[hsl(var(--primary))]">{asset.name}</code>
        <div className="text-xs text-muted-foreground">
          {asset.width && asset.height ? `${asset.width}×${asset.height}` : null}
          {asset.width && asset.bytes ? ' · ' : null}
          {formatBytes(asset.bytes)}
        </div>
        {Array.isArray(asset.tags) && asset.tags.length ? (
          <div className="flex flex-wrap gap-1">
            {asset.tags.map((tag) => (
              <span
                key={tag}
                className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {rows.length ? (
          <div>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-1 text-xs uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
              data-testid={`brand-asset-toggle-${asset.name}`}
            >
              {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {open ? t('brand.hideRules') : t('brand.showRules')}
            </button>
            {open ? (
              <dl className="mt-2 space-y-1.5 text-sm">
                {rows.map((r, i) => (
                  <div key={i}>
                    {r.key ? (
                      <dt className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                        {r.key}
                      </dt>
                    ) : null}
                    <dd className="text-foreground leading-snug">{r.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        ) : null}

        <div className="mt-auto flex gap-2 pt-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!link}
            onClick={copyUrl}
            data-testid={`brand-asset-copy-${asset.name}`}
          >
            {copied ? <Check size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
            {copied ? t('brand.copied') : t('brand.copyUrl')}
          </Button>
          {link ? (
            <Button asChild size="sm" variant="ghost">
              <a href={link} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} className="mr-1.5" />
                {t('brand.open')}
              </a>
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function RequestRow({ row, t }) {
  const versions = Array.isArray(row.assets) ? row.assets.length : 0;
  return (
    <div className="space-y-1 text-sm" data-testid={`design-request-${row.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={REQUEST_TONE[row.status] || REQUEST_TONE.rejected}>
          {t(`brand.requestStatus.${row.status}`, row.status)}
        </Badge>
        <Badge variant="outline" className="text-muted-foreground">
          {t(`brand.requestKind.${row.kind}`, row.kind)}
        </Badge>
        <span className="ml-auto text-xs text-muted-foreground">{formatWhen(row.created_at)}</span>
      </div>
      <p className="leading-snug text-foreground">{row.purpose}</p>
      <p className="text-xs text-muted-foreground">
        {row.kind === 'render' && row.template ? `${row.template} · ` : `${row.size} · `}
        {versions
          ? t('brand.versions', { n: versions })
          : t('brand.versionsRequested', { n: row.variants })}
        {Number(row.cost_usd) > 0 ? ` · $${Number(row.cost_usd).toFixed(2)}` : ''}
      </p>
      {row.status === 'ready' ? (
        <Link
          to="/dashboard/approvals"
          className="text-xs text-[hsl(var(--primary))] underline-offset-4 hover:underline"
        >
          {t('brand.waitingApproval')}
        </Link>
      ) : null}
      {row.status === 'failed' && row.error ? (
        <p className="text-xs text-red-600 break-words">{row.error}</p>
      ) : null}
    </div>
  );
}

export default function BrandLibraryPage() {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [signed, setSigned] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [business, setBusiness] = useState(null);
  const [kind, setKind] = useState(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const [a, r] = await Promise.all([fetchBrandAssets(), fetchDesignRequests(20)]);
        setAssets(a);
        setRequests(r);
        setSigned(await signAssetPaths(a.flatMap(assetPaths)));
      } catch (e) {
        setError(e?.message || t('brand.loadError'));
      } finally {
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const businesses = useMemo(() => distinctBusinesses(assets), [assets]);
  const kindsPresent = useMemo(
    () => ASSET_KINDS.filter((k) => assets.some((a) => a.kind === k)),
    [assets]
  );
  const visible = useMemo(
    () => filterAssets(assets, { business, kind }),
    [assets, business, kind]
  );

  return (
    <div data-testid="brand-library-page">
      <PageHeader
        eyebrow={t('brand.eyebrow')}
        title={t('brand.title')}
        subtitle={t('brand.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="brand-refresh"
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('brand.refresh')}
          </Button>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="brand-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <div className="space-y-4 xl:col-span-3">
            <div className="flex flex-wrap items-center gap-2" data-testid="brand-filters">
              <FilterChip
                active={!business}
                onClick={() => setBusiness(null)}
                testId="brand-filter-all"
              >
                {t('brand.all')} ({assets.length})
              </FilterChip>
              {businesses.map((b) => (
                <FilterChip
                  key={b}
                  active={business === b}
                  onClick={() => setBusiness(business === b ? null : b)}
                >
                  {b}
                </FilterChip>
              ))}
              {kindsPresent.length > 1 ? (
                <>
                  <span className="mx-1 h-4 w-px bg-border" />
                  {kindsPresent.map((k) => (
                    <FilterChip
                      key={k}
                      active={kind === k}
                      onClick={() => setKind(kind === k ? null : k)}
                    >
                      {t(`brand.kind.${k}`, k)}
                    </FilterChip>
                  ))}
                </>
              ) : null}
            </div>

            {visible.length === 0 ? (
              <Card data-testid="brand-empty">
                <CardContent className="py-10 text-center text-muted-foreground">
                  <Images className="mx-auto mb-3 h-8 w-8 opacity-60" />
                  <p className="text-sm">{t('brand.empty')}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {visible.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    url={displayUrl(asset, signed)}
                    fileUrl={mediaUrl(asset, signed)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <Card data-testid="brand-queue">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-[0.14em] text-muted-foreground">
                  {t('brand.queueTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {requests.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('brand.queueEmpty')}</p>
                ) : (
                  requests.map((row) => <RequestRow key={row.id} row={row} t={t} />)
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
